<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import * as THREE from 'three'
import { EXRLoader } from 'three/addons/loaders/EXRLoader.js'
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js'
import { createConfiguredGltfLoader, disposeSharedGltfDecoders } from '@/rendering/createGltfLoader'

import { clampMaterialOpacity } from '@/animation/clampMaterialOpacity'
import { evaluateLayerAtTime } from '@/animation/evaluateLayerAtTime'
import { evaluateLayerFrameAtGlobalTime } from '@/animation/evaluateLayerFrameAtGlobalTime'
import { isPlayheadInsideClipInterior } from '@/animation/timeline/clipEndpoints'
import {
  CLASSIC_EDITOR_FILL_LIGHT_INTENSITY,
  CLASSIC_EDITOR_HEMISPHERE_GROUND,
  CLASSIC_EDITOR_HEMISPHERE_SKY,
  getLightingPresetDefinition,
  resolvedDirectionalIntensity,
  resolvedHemisphereIntensity,
  type LightingPresetDefinition,
} from '@/constants/lightingPresets'
import { MOCKUP_DEVICE_TEMPLATES, type MockupDeviceTemplate } from '@/constants/mockupDevices'
import { bakeClassicRoomEnvironmentPmrem } from '@/rendering/classicRoomEnvironment'
import {
  applyDiffuseIblOnlyPolicy,
  applyDiffuseIblOnlyPolicyOnTree,
} from '@/rendering/iblDiffuseOnlyMaterialPatch'
import { enhanceProductMaterialsOnTree } from '@/rendering/enhanceProductMaterials'
import {
  applySnapshotToScreenMesh,
  bindMapAndEmissiveToScreenMesh,
  resolveVideoTimeForLayer,
  runWhenVideoHasDrawableDimensions,
  SCREEN_USERDATA,
  snapshotScreenMeshMaterials,
  waitVideoSeeked,
  type ScreenMaterialSlotSnapshot,
} from '@/rendering/screenTexture/screenMaterialRuntime'
import {
  disposeScreenRasterTransitionState,
  syncScreenRasterTextureWithTransition,
} from '@/rendering/screenTexture/screenRasterTransition'
import { ensureScreenTransitionPatchOnMesh } from '@/rendering/screenTexture/screenStandardMaterialTransition'
import {
  resolvedLayerScreenImageTransitionMode,
  SCREEN_IMAGE_TRANSITION_NONE,
  type ScreenImageTransitionModeId,
} from '@/constants/screenImageTransition'
import { resolveScreenImageIdAtGlobalTime } from '@/animation/resolveScreenImageIdAtGlobalTime'
import { effectiveScreenRasterObjectUrl } from '@/rendering/screenTexture/effectiveScreenRasterUrl'
import type { ExportRenderQualityId } from '@/rendering/export/types'
import { setViewportExportApi, type ExportVisualSessionOptions } from '@/rendering/viewportExportBridge'
import { deepCloneLayer } from '@/editor/history/cloneUtils'
import type { ViewportHandlers } from '@/stores/mockupEditor'
import { useMockupEditorStore } from '@/stores/mockupEditor'
import type { AnimationLayer } from '@/types/animationLayer'
import { createBackgroundGradientCanvasTexture } from '@/utils/gradient'
import { isScreenMaterialFileAcceptedForLayer } from '@/utils/mockupScreenMaterialFile'
import defaultImagePlaneTextureUrl from '@/assets/models/Other/texture.jpg?url'

const host = ref<HTMLElement | null>(null)
const store = useMockupEditorStore()

let renderer: THREE.WebGLRenderer | null = null
let scene: THREE.Scene | null = null
let camera: THREE.Camera | null = null
let layersRoot: THREE.Group | null = null
const layerGroups = new Map<string, THREE.Group>()
const objectLayerRegistry = new WeakMap<THREE.Object3D, string>()
const layerSelectionBoxes = new Map<string, THREE.BoxHelper>()
/** 导出视觉会话嵌套深度；仅最外层切换 {@link suppressEditorOverlayForExport} 与渲染质量 */
let exportVisualDepth = 0
/** 为 true 时关闭选中高亮（正式导出） */
let suppressEditorOverlayForExport = false
/** 导出前保存的 `shadowMap.enabled`，用于恢复 */
let savedShadowMapEnabled: boolean | null = null
let raf = 0
let bgTexture: THREE.CanvasTexture | null = null
/** 图片背景专用纹理（cover 缩放依赖原始宽高，需要与 gradient 分开管理） */
let bgImageTexture: THREE.Texture | null = null
/** 当前图片背景的原始像素宽高（用于 cover 缩放） */
let bgImageNaturalSize: { w: number; h: number } | null = null

const initialCamPos = new THREE.Vector3(0, 0.2, 4.25)
const initialTarget = new THREE.Vector3(0, 0, 0)

/**
 * 正交相机「纵向可视范围」(world units)。值越大，画面越“远”（物体越小）。
 * 你们的图层/机模尺寸量级大约在 2~3 之间（如 image-plane 最长边约 3），因此给一点边距。
 */
const ORTHO_VIEW_HEIGHT = 3.6

/**
 * 按画布宽高更新相机投影矩阵（透视：aspect；正交：left/right/top/bottom）。
 *
 * @param w - 画布像素宽
 * @param h - 画布像素高
 */
function syncCameraProjectionForSize(w: number, h: number): void {
  if (!camera) return
  const ww = Math.max(1, w)
  const hh = Math.max(1, h)
  const aspect = ww / hh

  if (camera instanceof THREE.PerspectiveCamera) {
    camera.aspect = aspect
    camera.updateProjectionMatrix()
    return
  }
  if (camera instanceof THREE.OrthographicCamera) {
    const halfH = ORTHO_VIEW_HEIGHT / 2
    const halfW = halfH * aspect
    camera.left = -halfW
    camera.right = halfW
    camera.top = halfH
    camera.bottom = -halfH
    camera.updateProjectionMatrix()
  }
}

/**
 * 当前图层是否显示编辑器选中高亮。
 *
 * @param layerId - 图层 id
 */
function isLayerVisuallySelected(layerId: string): boolean {
  return !suppressEditorOverlayForExport && layerId === store.selectedLayerId
}

let stopBgWatch: (() => void) | null = null
let stopLayerWatch: (() => void) | null = null
let stopTimeWatch: (() => void) | null = null
let stopTransformToolWatch: (() => void) | null = null
let stopClearToolWhenNoLayerWatch: (() => void) | null = null
let stopLightingWatch: (() => void) | null = null

/** 半球光 / 平行光（强度随灯光预设变化） */
let hemiLight: THREE.HemisphereLight | null = null
let dirLight: THREE.DirectionalLight | null = null
let pmremGenerator: THREE.PMREMGenerator | null = null
/** `fromEquirectangular` 生成的目标；切换或卸载时需 `dispose` */
let pmremTarget: THREE.WebGLRenderTarget | null = null
/** 递增以丢弃过期的 HDR 异步回调 */
let lightingLoadGen = 0

const raycaster = new THREE.Raycaster()
const ndc = new THREE.Vector2()
const movePlane = new THREE.Plane()
const planeHit = new THREE.Vector3()
const tmpWorld = new THREE.Vector3()
const camDir = new THREE.Vector3()

/**
 * 视口指针拖拽：把求值姿态烘焙到图层字段后，按工具类型增量写入；仅在片段首尾写入端点关键帧。
 */
type ViewportLayerDragState = {
  tool: 'move' | 'rotate' | 'scale'
  pointerId: number
  el: HTMLElement
  layerId: string
  /** 拖拽开始前图层深拷贝（指针按下并烘焙姿态后） */
  layerBefore: AnimationLayer
  /** 移动：射线与平面首次交点（世界空间） */
  moveHit0: THREE.Vector3
  /** 移动：与 `movePlane` 一致，拖拽全程固定 */
  movePlaneClone: THREE.Plane
  startPos: THREE.Vector3
  startRot: THREE.Vector3
  startScale: THREE.Vector3
  /** 按下时指针（屏幕像素），用于旋转/缩放累计 */
  anchorClientX: number
  anchorClientY: number
}

let layerDrag: ViewportLayerDragState | null = null
let isViewportPointerInside = false

/**
 * 将当前时刻求值姿态写入图层字段并提交播放头附近的片段端点（若有），使拖拽起点与渲染一致。
 *
 * @param layer - 选中图层
 */
function bakeEvaluatedPoseIntoLayer(layer: AnimationLayer): void {
  const pose = evaluateLayerAtTime(layer, store.currentTimeSeconds)
  layer.position.x = pose.position.x
  layer.position.y = pose.position.y
  layer.position.z = pose.position.z
  layer.rotationDeg.x = pose.rotationDeg.x
  layer.rotationDeg.y = pose.rotationDeg.y
  layer.rotationDeg.z = pose.rotationDeg.z
  layer.scale.x = pose.scale.x
  layer.scale.y = pose.scale.y
  layer.scale.z = pose.scale.z
  store.commitActiveLayerPoseAtPlayhead()
  applyLayerPosesAtTime(store.currentTimeSeconds)
}

/**
 * 按当前变换工具更新画布光标（未拖拽时）。
 */
function syncCanvasCursorFromTool(): void {
  if (!renderer) return
  if (layerDrag) return
  const t = store.viewportTransformTool
  const el = renderer.domElement
  if (t === 'move') el.style.cursor = 'grab'
  else if (t === 'rotate') el.style.cursor = 'all-scroll'
  else if (t === 'scale') el.style.cursor = 'ns-resize'
  else el.style.cursor = ''
}

/**
 * 将相机固定为产品默认视角（禁止轨道/缩放等改动相机）。
 */
function enforceFixedCamera(): void {
  if (!camera) return
  camera.position.copy(initialCamPos)
  camera.lookAt(initialTarget)
}

function ndcFromPointer(e: PointerEvent, rect: DOMRect): void {
  ndc.x = ((e.clientX - rect.left) / Math.max(1, rect.width)) * 2 - 1
  ndc.y = -((e.clientY - rect.top) / Math.max(1, rect.height)) * 2 + 1
}

/**
 * 将客户端坐标转换为 NDC（供鼠标双击拾取复用）。
 *
 * @param clientX - 客户端 x
 * @param clientY - 客户端 y
 * @param rect - 画布矩形
 */
function ndcFromClient(clientX: number, clientY: number, rect: DOMRect): void {
  ndc.x = ((clientX - rect.left) / Math.max(1, rect.width)) * 2 - 1
  ndc.y = -((clientY - rect.top) / Math.max(1, rect.height)) * 2 + 1
}

/**
 * 为对象子树写入 layer 归属，支持子 mesh 命中后回溯到图层。
 *
 * @param root - 对象根节点
 * @param layerId - 图层 id
 */
function registerLayerOwnership(root: THREE.Object3D, layerId: string): void {
  root.userData.layerId = layerId
  objectLayerRegistry.set(root, layerId)
  root.traverse((obj) => {
    obj.userData.layerId = layerId
    objectLayerRegistry.set(obj, layerId)
  })
}

/**
 * 根据命中的 Object3D 解析所属 layer id（优先 userData，其次弱映射，最后回溯到 layer 根组）。
 *
 * @param hitObject - 射线命中对象
 */
function resolveLayerIdFromHit(hitObject: THREE.Object3D): string | null {
  let cur: THREE.Object3D | null = hitObject
  while (cur) {
    const byUserData = cur.userData.layerId
    if (typeof byUserData === 'string' && byUserData.length > 0) return byUserData
    const byRegistry = objectLayerRegistry.get(cur)
    if (byRegistry) return byRegistry
    cur = cur.parent
  }
  for (const [layerId, group] of layerGroups) {
    if (hitObject === group || group.children.includes(hitObject)) return layerId
  }
  return null
}

/**
 * 更新图层选中框显隐与样式。
 *
 * @param layerId - 图层 id
 * @param group - 图层根组
 * @param selected - 是否选中
 */
function applySelectionBox(layerId: string, group: THREE.Group, selected: boolean): void {
  const existed = layerSelectionBoxes.get(layerId)
  if (!selected || !group.visible) {
    if (existed) existed.visible = false
    return
  }
  let helper = existed
  if (!helper) {
    helper = new THREE.BoxHelper(group, 0x9ca3af)
    helper.material.depthTest = true
    helper.material.depthWrite = false
    helper.material.transparent = true
    helper.material.opacity = 0.95
    helper.renderOrder = 0
    layerSelectionBoxes.set(layerId, helper)
    layersRoot?.add(helper)
  }
  helper.visible = true
  helper.update()
}

/**
 * 同步各图层选中 BoxHelper 的可见性与几何。
 * 常规预览在 {@link tick} 中每帧更新；导出时 `sceneCanvasExportDepth > 0` 会跳过 `tick`，必须在离屏渲染前调用，否则外框仍停留在上一帧姿态或不可见。
 */
function syncLayerSelectionBoxesForRender(): void {
  for (const [layerId, helper] of layerSelectionBoxes) {
    const group = layerGroups.get(layerId)
    helper.visible = Boolean(group?.visible) && isLayerVisuallySelected(layerId)
    if (helper.visible) helper.update()
  }
}

/**
 * 双击画布命中模型并切换当前操作图层。
 *
 * @param e - 双击事件
 */
/**
 * 按屏幕坐标射线拾取第一个动画图层 id（与双击选层共用）。
 *
 * @param clientX - 视口客户端 x
 * @param clientY - 视口客户端 y
 */
function pickLayerIdFromClient(clientX: number, clientY: number): string | null {
  if (!renderer || !camera) return null
  const roots = [...layerGroups.values()]
  if (roots.length === 0) return null
  const rect = renderer.domElement.getBoundingClientRect()
  ndcFromClient(clientX, clientY, rect)
  raycaster.setFromCamera(ndc, camera)
  const hits = raycaster.intersectObjects(roots, true)
  for (const hit of hits) {
    const layerId = resolveLayerIdFromHit(hit.object)
    if (!layerId) continue
    if (!store.animationLayers.some((layer) => layer.id === layerId)) continue
    return layerId
  }
  return null
}

function onCanvasDoubleClickSelectLayer(e: MouseEvent): void {
  if (!renderer || !camera || layerDrag || sceneCanvasExportDepth > 0) return
  const roots = [...layerGroups.values()]
  if (roots.length === 0) {
    store.selectAnimationLayer(null)
    return
  }
  store.selectAnimationLayer(pickLayerIdFromClient(e.clientX, e.clientY))
}

/** 操作系统文件拖入：与 `dragenter` 嵌套配对，用于高亮边框 */
let fileDragHoverDepth = 0

function viewportHasFilePayload(dt: DataTransfer | null): boolean {
  return !!dt?.types.includes('Files')
}

/**
 * 视口拖放屏幕贴图：与属性面板共用 store（`setActiveLayerScreenMaterial`），松开时按射线命中图层。
 */
function onViewportHostDragEnter(e: DragEvent): void {
  if (!viewportHasFilePayload(e.dataTransfer)) return
  if (layerDrag || sceneCanvasExportDepth > 0) return
  e.preventDefault()
  e.stopPropagation()
  fileDragHoverDepth += 1
  if (fileDragHoverDepth === 1) host.value?.classList.add('fileDropHover')
}

function onViewportHostDragLeave(e: DragEvent): void {
  if (!viewportHasFilePayload(e.dataTransfer)) return
  e.preventDefault()
  e.stopPropagation()
  fileDragHoverDepth = Math.max(0, fileDragHoverDepth - 1)
  if (fileDragHoverDepth === 0) host.value?.classList.remove('fileDropHover')
}

function onViewportHostDragOver(e: DragEvent): void {
  if (!viewportHasFilePayload(e.dataTransfer)) return
  if (layerDrag || sceneCanvasExportDepth > 0) return
  e.preventDefault()
  e.stopPropagation()
  e.dataTransfer!.dropEffect = 'copy'
}

function onViewportHostDrop(e: DragEvent): void {
  fileDragHoverDepth = 0
  host.value?.classList.remove('fileDropHover')
  if (!viewportHasFilePayload(e.dataTransfer)) return
  e.preventDefault()
  e.stopPropagation()
  if (layerDrag || sceneCanvasExportDepth > 0) return
  if (!renderer || !camera) return
  const layerId = pickLayerIdFromClient(e.clientX, e.clientY)
  if (!layerId) {
    store.setStubMessage(store.tUi('editorMessages.viewportDropNoLayerHit'))
    return
  }
  const layer = store.animationLayers.find((l) => l.id === layerId) ?? null
  if (!layer) return
  if (layer.deviceTemplateId === 'text-plane') {
    store.setStubMessage(store.tUi('editorMessages.viewportDropTextPlaneNotSupported'))
    return
  }
  const raw = e.dataTransfer?.files ?? null
  const list = raw ? Array.from(raw) : []
  const accepted = list.filter((f) => isScreenMaterialFileAcceptedForLayer(layer.deviceTemplateId, f))
  if (accepted.length === 0) {
    store.setStubMessage(store.tUi('editorMessages.viewportDropInvalidFile'))
    return
  }
  if (layer.locked) {
    store.setStubMessage(
      store.tUi('editorMessages.lockedCannot', {
        action: store.tUi('editorActions.editScreenMaterial'),
      }),
    )
    return
  }
  store.selectAnimationLayer(layerId)
  void store.ingestScreenMaterialFileList(accepted)
}

function onLayerToolPointerDown(e: PointerEvent): void {
  const tool = store.viewportTransformTool
  if (tool === null || e.button !== 0) return
  const layer = store.activeLayer
  if (!layer || !renderer || !camera) return
  if (layer.locked) {
    store.setStubMessage(
      store.tUi('editorMessages.lockedCannot', {
        action: store.tUi('editorActions.useViewportTransformTool'),
      }),
    )
    return
  }
  const group = layerGroups.get(layer.id)
  if (!group) return
  if (isPlayheadInsideClipInterior(layer.clips, store.currentTimeSeconds)) {
    store.setStubMessage('播放头位于片段内部时无法使用变换工具，请移到片段首尾或空白区域。')
    return
  }

  e.preventDefault()
  const el = renderer.domElement
  el.setPointerCapture(e.pointerId)

  bakeEvaluatedPoseIntoLayer(layer)
  const layerBefore = deepCloneLayer(layer)

  ndcFromPointer(e, el.getBoundingClientRect())
  raycaster.setFromCamera(ndc, camera)
  group.getWorldPosition(tmpWorld)
  camera.getWorldDirection(camDir)
  movePlane.setFromNormalAndCoplanarPoint(camDir, tmpWorld)

  if (tool === 'move') {
    const hit0 = new THREE.Vector3()
    const pc = movePlane.clone()
    if (!raycaster.ray.intersectPlane(pc, hit0)) {
      el.releasePointerCapture(e.pointerId)
      return
    }
    layerDrag = {
      tool: 'move',
      pointerId: e.pointerId,
      el,
      layerId: layer.id,
      layerBefore,
      moveHit0: hit0.clone(),
      movePlaneClone: pc,
      startPos: new THREE.Vector3(layer.position.x, layer.position.y, layer.position.z),
      startRot: new THREE.Vector3(layer.rotationDeg.x, layer.rotationDeg.y, layer.rotationDeg.z),
      startScale: new THREE.Vector3(layer.scale.x, layer.scale.y, layer.scale.z),
      anchorClientX: e.clientX,
      anchorClientY: e.clientY,
    }
    el.style.cursor = 'grabbing'
  } else if (tool === 'rotate') {
    layerDrag = {
      tool: 'rotate',
      pointerId: e.pointerId,
      el,
      layerId: layer.id,
      layerBefore,
      moveHit0: new THREE.Vector3(),
      movePlaneClone: new THREE.Plane(),
      startPos: new THREE.Vector3(layer.position.x, layer.position.y, layer.position.z),
      startRot: new THREE.Vector3(layer.rotationDeg.x, layer.rotationDeg.y, layer.rotationDeg.z),
      startScale: new THREE.Vector3(layer.scale.x, layer.scale.y, layer.scale.z),
      anchorClientX: e.clientX,
      anchorClientY: e.clientY,
    }
    el.style.cursor = 'grabbing'
  } else {
    layerDrag = {
      tool: 'scale',
      pointerId: e.pointerId,
      el,
      layerId: layer.id,
      layerBefore,
      moveHit0: new THREE.Vector3(),
      movePlaneClone: new THREE.Plane(),
      startPos: new THREE.Vector3(layer.position.x, layer.position.y, layer.position.z),
      startRot: new THREE.Vector3(layer.rotationDeg.x, layer.rotationDeg.y, layer.rotationDeg.z),
      startScale: new THREE.Vector3(layer.scale.x, layer.scale.y, layer.scale.z),
      anchorClientX: e.clientX,
      anchorClientY: e.clientY,
    }
    el.style.cursor = 'ns-resize'
  }
}

function onLayerToolPointerMove(e: PointerEvent): void {
  if (!layerDrag || e.pointerId !== layerDrag.pointerId) return
  const d = layerDrag
  const layer = store.animationLayers.find((l) => l.id === d.layerId)
  if (!layer || !camera || !renderer) return
  const rect = d.el.getBoundingClientRect()
  const dx = e.clientX - d.anchorClientX
  const dy = e.clientY - d.anchorClientY
  const shiftLockAxis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y'

  if (d.tool === 'move') {
    ndcFromPointer(e, rect)
    raycaster.setFromCamera(ndc, camera)
    if (!raycaster.ray.intersectPlane(d.movePlaneClone, planeHit)) return
    const nextX = d.startPos.x + (planeHit.x - d.moveHit0.x)
    const nextY = d.startPos.y + (planeHit.y - d.moveHit0.y)
    const nextZ = d.startPos.z + (planeHit.z - d.moveHit0.z)
    if (e.shiftKey) {
      if (shiftLockAxis === 'x') {
        layer.position.x = nextX
        layer.position.y = d.startPos.y
      } else {
        layer.position.x = d.startPos.x
        layer.position.y = nextY
      }
      layer.position.z = d.startPos.z
    } else {
      layer.position.x = nextX
      layer.position.y = nextY
      layer.position.z = nextZ
    }
  } else if (d.tool === 'rotate') {
    /** 水平拖动偏航、垂直拖动俯仰；不夹紧角度以支持多圈（如 720°+） */
    const nextRotY = d.startRot.y + dx * 0.42
    const nextRotX = d.startRot.x - dy * 0.42
    if (e.shiftKey) {
      if (shiftLockAxis === 'x') {
        layer.rotationDeg.y = nextRotY
        layer.rotationDeg.x = d.startRot.x
      } else {
        layer.rotationDeg.x = nextRotX
        layer.rotationDeg.y = d.startRot.y
      }
    } else {
      layer.rotationDeg.y = nextRotY
      layer.rotationDeg.x = nextRotX
    }
  } else {
    /** 指针上移放大、下移缩小；相对按下位置的纵向累计 */
    const u = Math.exp(-(e.clientY - d.anchorClientY) * 0.0038)
    layer.scale.x = Math.max(0.01, Math.min(25, d.startScale.x * u))
    layer.scale.y = Math.max(0.01, Math.min(25, d.startScale.y * u))
    layer.scale.z = Math.max(0.01, Math.min(25, d.startScale.z * u))
  }
  store.commitActiveLayerPoseAtPlayhead()
  applyLayerPosesAtTime(store.currentTimeSeconds)
}

/**
 * 结束单次视口变换拖拽。
 *
 * @param e - 指针抬起或取消事件
 */
function onLayerToolPointerUp(e: PointerEvent): void {
  if (!layerDrag || e.pointerId !== layerDrag.pointerId) return
  const d = layerDrag
  const cur = store.animationLayers.find((l) => l.id === d.layerId)
  if (cur) {
    const after = deepCloneLayer(cur)
    const label =
      d.tool === 'move' ? 'Viewport move' : d.tool === 'rotate' ? 'Viewport rotate' : 'Viewport scale'
    store.recordEditorLayerGestureIfChanged(d.layerId, d.layerBefore, after, label)
  }
  try {
    d.el.releasePointerCapture(e.pointerId)
  } catch {
    /* 可能已释放 */
  }
  layerDrag = null
  syncCanvasCursorFromTool()
}

/**
 * `Escape` 关闭变换工具（工具栏不再用「再点同一项」关闭，避免误触）。
 *
 * @param e - 键盘事件
 */
function onViewportTransformEscape(e: KeyboardEvent): void {
  if (e.key !== 'Escape') return
  if (store.viewportTransformTool === null) return
  e.preventDefault()
  store.setViewportTransformTool(null)
}

/**
 * 在 viewport 上下文内处理工具热键（W/E/R）。
 *
 * @param e - 键盘事件
 */
function onViewportToolHotkeys(e: KeyboardEvent): void {
  if (!isViewportPointerInside) return
  const activeEl = document.activeElement as HTMLElement | null
  if (activeEl) {
    const tag = activeEl.tagName
    if (activeEl.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
  }
  if (e.ctrlKey || e.metaKey || e.altKey) return
  if (e.code === 'KeyW') {
    e.preventDefault()
    if (store.selectedLayerId && !store.isLayerLocked(store.selectedLayerId)) {
      store.setViewportTransformTool('move')
    }
    return
  }
  if (e.code === 'KeyE') {
    e.preventDefault()
    if (store.selectedLayerId && !store.isLayerLocked(store.selectedLayerId)) {
      store.setViewportTransformTool('rotate')
    }
    return
  }
  if (e.code === 'KeyR') {
    e.preventDefault()
    if (store.selectedLayerId && !store.isLayerLocked(store.selectedLayerId)) {
      store.setViewportTransformTool('scale')
    }
  }
}

/**
 * 标记鼠标进入 viewport 区域，启用作用域热键。
 */
function onViewportPointerEnter(): void {
  isViewportPointerInside = true
}

/**
 * 标记鼠标离开 viewport 区域，禁用作用域热键。
 */
function onViewportPointerLeave(): void {
  isViewportPointerInside = false
}

function parseHex(hex: string): string {
  const h = hex.trim()
  if (h.startsWith('#') && (h.length === 7 || h.length === 9)) return h.slice(0, 7)
  if (h.startsWith('#') && h.length === 4) {
    const r = h[1]
    const g = h[2]
    const b = h[3]
    return `#${r}${r}${g}${g}${b}${b}`
  }
  return '#ffffff'
}

function disposeBgTexture(): void {
  if (bgTexture) {
    bgTexture.dispose()
    bgTexture = null
  }
  if (bgImageTexture) {
    bgImageTexture.dispose()
    bgImageTexture = null
  }
  bgImageNaturalSize = null
}

/**
 * 按 renderer 当前缓冲尺寸对 {@link bgImageTexture} 设置 cover 式 `repeat/offset`，
 * 等价于 CSS `background-size: cover` + `background-position: center`。
 *
 * 仅在 `backgroundMode === 'image'` 时有效；resize / beginSceneCanvasSize 后需要调用。
 */
function refreshBackgroundImageCover(): void {
  if (!renderer || !bgImageTexture || !bgImageNaturalSize) return
  const size = new THREE.Vector2()
  renderer.getSize(size)
  const rw = Math.max(1, size.x)
  const rh = Math.max(1, size.y)
  const { w: iw, h: ih } = bgImageNaturalSize
  if (iw < 1 || ih < 1) return
  const iar = iw / ih
  const rar = rw / rh
  /** cover：较窄一边填满，另一边按比例裁切 */
  const repeatX = iar > rar ? rar / iar : 1
  const repeatY = iar > rar ? 1 : iar / rar
  bgImageTexture.wrapS = THREE.ClampToEdgeWrapping
  bgImageTexture.wrapT = THREE.ClampToEdgeWrapping
  bgImageTexture.repeat.set(repeatX, repeatY)
  bgImageTexture.offset.set((1 - repeatX) / 2, (1 - repeatY) / 2)
  bgImageTexture.needsUpdate = true
}

/**
 * 异步加载背景图并挂到 `scene.background`，完成后刷新 cover 缩放。
 *
 * @param url - blob URL 或远程 URL
 */
function loadBackgroundImageTexture(url: string): void {
  if (!scene || !renderer) return
  const loader = new THREE.TextureLoader()
  loader.load(
    url,
    (tex) => {
      if (!scene) {
        tex.dispose()
        return
      }
      /** 若回调回来时用户已切到其他模式 / 换了图，直接作废 */
      if (store.backgroundMode !== 'image' || store.backgroundImageSrc !== url) {
        tex.dispose()
        return
      }
      if (bgImageTexture) bgImageTexture.dispose()
      bgImageTexture = tex
      tex.colorSpace = THREE.SRGBColorSpace
      tex.anisotropy = renderer?.capabilities.getMaxAnisotropy() ?? 1
      tex.minFilter = THREE.LinearFilter
      tex.magFilter = THREE.LinearFilter
      const img = tex.image as HTMLImageElement | ImageBitmap | undefined
      const w = (img as HTMLImageElement | undefined)?.naturalWidth ?? (img as ImageBitmap | undefined)?.width ?? 0
      const h = (img as HTMLImageElement | undefined)?.naturalHeight ?? (img as ImageBitmap | undefined)?.height ?? 0
      bgImageNaturalSize = { w, h }
      refreshBackgroundImageCover()
      scene.background = tex
    },
    undefined,
    () => {
      /** 加载失败时回退到白色，避免视口空白 */
      if (!scene) return
      scene.background = new THREE.Color('#ffffff')
    },
  )
}

function applyBackground(): void {
  if (!scene || !renderer) return
  disposeBgTexture()
  const mode = store.backgroundMode
  if (mode === 'transparent') {
    scene.background = null
    renderer.setClearColor(0x000000, 0)
    return
  }
  renderer.setClearColor(0x000000, 1)
  if (mode === 'gradient') {
    bgTexture = createBackgroundGradientCanvasTexture(store.sortedGradientStops)
    scene.background = bgTexture
    return
  }
  if (mode === 'image') {
    const url = store.backgroundImageSrc
    if (url) {
      loadBackgroundImageTexture(url)
    } else {
      /** 尚未上传图片时以白色占位；BackgroundSection 已提示用户上传 */
      scene.background = new THREE.Color('#ffffff')
    }
    return
  }
  try {
    scene.background = new THREE.Color(parseHex(store.backgroundHex))
  } catch {
    scene.background = new THREE.Color('#ffffff')
  }
}

/**
 * 释放当前 PMREM 环境贴图并从场景解绑（不改变用户背景 `scene.background`）。
 */
function disposeSceneEnvironment(): void {
  if (pmremTarget) {
    pmremTarget.dispose()
    pmremTarget = null
  }
  if (scene) scene.environment = null
}

/**
 * HDR 预设为「仅漫反射 IBL」；经典光（RoomEnvironment）保留完整镜面 IBL，与 Three.js 编辑器一致。
 */
function usesDiffuseIblOnlyMaterialPatch(): boolean {
  return getLightingPresetDefinition(store.lightingPresetId).id !== 'classic'
}

/**
 * 对图层子树同步 IBL 材质补丁策略（切换灯光预设或加载 glTF 后调用）。
 *
 * @param root - 图层 `Group` 或 glTF 根
 */
function syncIblMaterialPatchOnSubtree(root: THREE.Object3D): void {
  applyDiffuseIblOnlyPolicyOnTree(root, usesDiffuseIblOnlyMaterialPatch())
}

/**
 * 对所有已挂载图层同步 IBL 材质补丁。
 */
function syncIblMaterialPatchOnAllLayers(): void {
  for (const group of layerGroups.values()) {
    syncIblMaterialPatchOnSubtree(group)
  }
}

/**
 * 经典光：写入 Three.js 编辑器同款 RoomEnvironment PMREM。
 */
function applyClassicRoomEnvironment(): void {
  if (!scene || !pmremGenerator) return
  disposeSceneEnvironment()
  pmremTarget = bakeClassicRoomEnvironmentPmrem(pmremGenerator)
  scene.environment = pmremTarget.texture
}

/**
 * 按预设更新半球光与平行光强度与方向。
 *
 * @param def - 灯光预设
 */
function applyFillLightsFromPreset(def: LightingPresetDefinition): void {
  if (!hemiLight || !dirLight) return
  if (def.id === 'classic') {
    hemiLight.color.setHex(CLASSIC_EDITOR_HEMISPHERE_SKY)
    hemiLight.groundColor.setHex(CLASSIC_EDITOR_HEMISPHERE_GROUND)
    hemiLight.intensity = CLASSIC_EDITOR_FILL_LIGHT_INTENSITY
    dirLight.intensity = CLASSIC_EDITOR_FILL_LIGHT_INTENSITY
  } else {
    const amb = store.viewportAmbientIntensityScale
    hemiLight.intensity = resolvedHemisphereIntensity(def) * amb
    dirLight.intensity = resolvedDirectionalIntensity(def)
  }
  dirLight.position.set(
    def.directionalPosition.x,
    def.directionalPosition.y,
    def.directionalPosition.z,
  )
}

/**
 * 同步曝光、环境强度与旋转（与 `scene.environment` 是否为 null 独立）。
 *
 * @param def - 灯光预设
 */
function applyRendererToneAndSceneEnvParams(def: LightingPresetDefinition): void {
  if (!renderer || !scene) return
  renderer.toneMapping = THREE.ACESFilmicToneMapping
  renderer.toneMappingExposure = def.toneMappingExposure
  if (def.id === 'classic') {
    scene.environmentIntensity = def.environmentIntensity
  } else {
    scene.environmentIntensity = def.environmentIntensity * store.viewportAmbientIntensityScale
  }
  scene.environmentRotation.set(0, def.environmentRotationY, 0)
}

/**
 * 根据 `store.lightingPresetId` 应用辅助光；按需异步加载环境图并写入 `scene.environment`。
 */
function syncLightingFromStore(): void {
  if (!scene || !renderer || !pmremGenerator || !hemiLight || !dirLight) return
  const pmrem = pmremGenerator

  const gen = (lightingLoadGen += 1)
  const def = getLightingPresetDefinition(store.lightingPresetId)

  applyFillLightsFromPreset(def)
  applyRendererToneAndSceneEnvParams(def)
  syncIblMaterialPatchOnAllLayers()

  if (def.useRoomEnvironment) {
    applyClassicRoomEnvironment()
    return
  }

  if (!def.envMapUrl) {
    disposeSceneEnvironment()
    return
  }

  const useExrLoader = /\.exr(?:$|\?)/i.test(def.envMapUrl)
  const envLoader = useExrLoader ? new EXRLoader() : new RGBELoader()
  envLoader.load(
    def.envMapUrl,
    (tex) => {
      if (gen !== lightingLoadGen) {
        tex.dispose()
        return
      }
      try {
        tex.mapping = THREE.EquirectangularReflectionMapping
        const rt = pmrem.fromEquirectangular(tex)
        tex.dispose()
        if (gen !== lightingLoadGen) {
          rt.dispose()
          return
        }
        disposeSceneEnvironment()
        pmremTarget = rt
        scene!.environment = rt.texture
      } catch {
        tex.dispose()
        if (gen === lightingLoadGen) handleEnvironmentLoadFailure(def)
      }
    },
    undefined,
    () => {
      if (gen !== lightingLoadGen) return
      handleEnvironmentLoadFailure(def)
    },
  )
}

/**
 * 环境图加载或 PMREM 失败：清空 IBL、提示用户并回退到「经典光」预设。
 *
 * @param def - 尝试加载的预设（用于文案）
 */
function handleEnvironmentLoadFailure(def: LightingPresetDefinition): void {
  disposeSceneEnvironment()
  store.setStubMessage(`灯光环境图加载失败（${def.label}），已切换为经典光。`)
  if (store.lightingPresetId !== 'classic') store.setLightingPreset('classic')
}

/**
 * 释放 Object3D 子树中的几何体与材质（用于丢弃未挂载的 glTF 场景）。
 *
 * @param root - 根节点
 */
function disposeObjectSubtree(root: THREE.Object3D): void {
  root.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      obj.geometry.dispose()
      const m = obj.material
      if (!Array.isArray(m)) m.dispose()
      else m.forEach((x) => x.dispose())
    }
  })
}

/**
 * 释放用户上传的 `VideoTexture` / 图片纹理与 `<video>`，不修改网格上 glTF 原始材质（由快照还原）。
 *
 * @param group - 图层 Group
 */
function releaseUploadedScreenResources(group: THREE.Group): void {
  /** 先复位过渡 uniform 与等待中的目标贴图；屏幕材质仍是 `MeshStandardMaterial` 不被替换。 */
  disposeScreenRasterTransitionState(group)
  const videoEl = group.userData[SCREEN_USERDATA.screenVideoEl] as HTMLVideoElement | undefined
  if (videoEl) {
    try {
      videoEl.pause()
    } catch {
      /* ignore */
    }
    videoEl.removeAttribute('src')
    videoEl.src = ''
    try {
      videoEl.load()
    } catch {
      /* ignore */
    }
    group.userData[SCREEN_USERDATA.screenVideoEl] = undefined
  }
  const screenMap = group.userData[SCREEN_USERDATA.screenMap] as THREE.Texture | undefined
  if (screenMap) {
    const smClear = group.userData.screenMesh as THREE.Mesh | undefined
    if (smClear) {
      const mats: THREE.Material[] = Array.isArray(smClear.material) ? smClear.material : [smClear.material]
      for (const m of mats) {
        if (m instanceof THREE.MeshStandardMaterial) {
          if (m.map === screenMap) m.map = null
          if (m.emissiveMap === screenMap) m.emissiveMap = null
          m.needsUpdate = true
        }
      }
    }
    screenMap.dispose()
    group.userData[SCREEN_USERDATA.screenMap] = undefined
  }
}

/**
 * 在首次获得屏幕 mesh 后捕获 glTF 默认材质，供清除用户贴图时还原。
 *
 * @param group - 图层 Group
 */
function ensureScreenMaterialSnapshot(group: THREE.Group): void {
  if (group.userData.screenMaterialSnapshot) return
  const screenMesh = group.userData.screenMesh as THREE.Mesh | undefined
  if (!screenMesh) return
  group.userData.screenMaterialSnapshot = snapshotScreenMeshMaterials(screenMesh)
}

/**
 * 将屏幕材质恢复为加入场景时的快照。
 *
 * @param group - 图层 Group
 */
function restoreScreenMaterialSnapshot(group: THREE.Group): void {
  const screenMesh = group.userData.screenMesh as THREE.Mesh | undefined
  const snaps = group.userData.screenMaterialSnapshot as ScreenMaterialSlotSnapshot[] | undefined
  if (!screenMesh || !snaps) return
  applySnapshotToScreenMesh(screenMesh, snaps)
}

function disposeGroupContents(group: THREE.Group): void {
  releaseUploadedScreenResources(group)
  group.userData.screenMaterialSnapshot = undefined
  group.userData.appliedTextureKey = undefined
  group.userData.appliedVideoParamKey = undefined
  group.userData.appliedTextKey = undefined
  group.userData.screenMesh = undefined
  group.userData.bodyMesh = undefined
  const loadId = (group.userData.textureLoadId as number | undefined) ?? 0
  group.userData.textureLoadId = loadId + 1
  group.userData.layerId = undefined
  /** 释放 Canvas 文字纹理 */
  const textTex = group.userData.textCanvasTexture as THREE.CanvasTexture | undefined
  if (textTex) {
    textTex.dispose()
    group.userData.textCanvasTexture = undefined
  }
  /** 释放 image-plane 默认贴图 */
  const defTex = group.userData.defaultTexture as THREE.Texture | undefined
  if (defTex) {
    defTex.dispose()
    group.userData.defaultTexture = undefined
  }
  group.userData.hasUserTexture = undefined

  while (group.children.length > 0) {
    const ch = group.children[0]!
    group.remove(ch)
    disposeObjectSubtree(ch)
  }
}

/**
 * 将模型包络缩放并居中到原点附近，使视口内尺寸与占位机（约 2.2）同一量级。
 *
 * @param root - glTF 根 `scene`
 * @param targetSize - 目标最大外包尺寸
 */
function fitAndCenterObject(root: THREE.Object3D, targetSize = 2.2): void {
  const box = new THREE.Box3().setFromObject(root)
  const size = new THREE.Vector3()
  box.getSize(size)
  const maxDim = Math.max(size.x, size.y, size.z, 1e-6)
  const s = targetSize / maxDim
  root.scale.setScalar(s)
  const box2 = new THREE.Box3().setFromObject(root)
  const center = new THREE.Vector3()
  box2.getCenter(center)
  root.position.sub(center)
}

/**
 * 在已加载的模型中挑选用于屏幕贴图的网格（配置名精确匹配优先，否则命名启发式，再取外包最大 mesh）。
 *
 * @param root - 模型根
 * @param preferredScreenName - 来自 {@link MockupDeviceTemplate.screenObjName}；`null`/空串则跳过精确匹配
 */
function pickScreenMesh(root: THREE.Object3D, preferredScreenName: string | null): THREE.Mesh | undefined {
  const meshes: THREE.Mesh[] = []
  root.traverse((o) => {
    if (o instanceof THREE.Mesh) meshes.push(o)
  })
  const pref = preferredScreenName?.trim()
  if (pref) {
    const exact = meshes.find((m) => m.name === pref)
    if (exact) return exact
  }
  const byName = meshes.find((m) => /screen|display|glass|lcd|canvas/i.test(m.name))
  if (byName) return byName
  let best: THREE.Mesh | undefined
  let bestScore = 0
  for (const m of meshes) {
    const g = m.geometry
    if (!g.boundingBox) g.computeBoundingBox()
    const bb = g.boundingBox
    if (!bb) continue
    const sz = new THREE.Vector3()
    bb.getSize(sz)
    const score = sz.x * sz.y + sz.y * sz.z + sz.z * sz.x
    if (score > bestScore) {
      bestScore = score
      best = m
    }
  }
  return best
}

/**
 * 异步加载 glTF 填入图层 Group；`modelLoadId` 不匹配时丢弃结果。
 *
 * @param group - 图层根
 * @param template - 设备模板
 * @param loadAttempt - 与 `group.userData.modelLoadId` 对齐
 * @param layer - 当前图层数据（用于贴图）
 */
function loadGltfModelIntoGroup(
  group: THREE.Group,
  template: MockupDeviceTemplate,
  loadAttempt: number,
  layer: AnimationLayer,
): void {
  const url = template.modelUrl
  if (!url) return
  store.startModelLoading()
  let loadSettled = false
  const settleLoad = (succeeded: boolean): void => {
    if (loadSettled) return
    loadSettled = true
    if (succeeded) store.finishModelLoading()
    else store.failModelLoading()
  }
  const loader = createConfiguredGltfLoader()
  loader.load(
    url,
    async (gltf) => {
      if (group.userData.modelLoadId !== loadAttempt) {
        disposeObjectSubtree(gltf.scene)
        settleLoad(false)
        return
      }
      const root = gltf.scene
      fitAndCenterObject(root, 2.2)
      registerLayerOwnership(root, layer.id)
      applyLayerFrameToGroup(group, layer, store.currentTimeSeconds)
      group.add(root)
      /**
       * 与 `three.js-187/examples/test2.html` → `enhanceMaterials` 对齐：
       * 提升 `envMapIntensity` 至 1.15，并修正部分压缩 glTF 中浅色外壳被
       * 误标为 `metalness = 1` 导致 IBL 下整片发黑的问题（典型受影响：
       * `iphoneAir-compressed.glb` 与 `macbook16-prom3-compressed.glb`
       * 的浅色背板）。
       */
      enhanceProductMaterialsOnTree(root)
      const screen = pickScreenMesh(root, template.screenObjName)
      group.userData.screenMesh = screen
      if (screen) ensureScreenTransitionPatchOnMesh(screen)
      /** glTF 多网格时不指定 `bodyMesh`：选中视觉完全由外层 BoxHelper 表现，不再改动机身材质 */
      group.userData.bodyMesh = undefined
      ensureScreenMaterialSnapshot(group)
      applyScreenTextureToGroup(group, layer)
      syncIblMaterialPatchOnSubtree(group)
      try {
        if (renderer && camera && scene) {
          root.updateWorldMatrix(true, true)
          await renderer.compileAsync(root, camera, scene)
        }
      } catch {
        /** Shader warm-up is an optimization; the loaded model remains usable if it is unsupported. */
      } finally {
        settleLoad(true)
      }
    },
    (ev: ProgressEvent<EventTarget>) => {
      if (group.userData.modelLoadId !== loadAttempt) return
      store.updateModelLoadProgress(ev.loaded, ev.total)
    },
    async () => {
      if (group.userData.modelLoadId !== loadAttempt) {
        settleLoad(false)
        return
      }
      const phone = buildPhoneGroup(template.id)
      applyLayerFrameToGroup(group, layer, store.currentTimeSeconds)
      while (phone.children.length > 0) {
        const ch = phone.children[0]!
        registerLayerOwnership(ch, layer.id)
        group.add(ch)
      }
      group.userData.bodyMesh = phone.userData.bodyMesh
      group.userData.screenMesh = phone.userData.screenMesh
      ensureScreenMaterialSnapshot(group)
      applyScreenTextureToGroup(group, layer)
      syncIblMaterialPatchOnSubtree(group)
      try {
        if (renderer && camera && scene) {
          group.updateWorldMatrix(true, true)
          await renderer.compileAsync(group, camera, scene)
        }
      } catch {
        /** Keep the procedural fallback usable when shader warm-up is unavailable. */
      } finally {
        settleLoad(false)
      }
    },
  )
}

/**
 * 按机型模板生成手机机身 + 正面屏幕平面（屏幕可贴图）。
 *
 * @param deviceTemplateId - `MOCKUP_DEVICE_TEMPLATES` 中的 id
 */
function buildPhoneGroup(deviceTemplateId: string): THREE.Group {
  const template = MOCKUP_DEVICE_TEMPLATES.find((t) => t.id === deviceTemplateId)
  const ar = template ? template.height / template.width : 2
  const w = 1.05
  const h = w * ar
  const d = 0.12

  const group = new THREE.Group()

  const bodyGeo = new THREE.BoxGeometry(w, h, d)
  const bodyMat = new THREE.MeshStandardMaterial({
    color: 0x14161a,
    roughness: 0.38,
    metalness: 0.08,
    emissive: 0x000000,
  })
  const bodyMesh = new THREE.Mesh(bodyGeo, bodyMat)
  group.add(bodyMesh)

  const sw = w * 0.86
  const sh = h * 0.9
  const screenGeo = new THREE.PlaneGeometry(sw, sh)
  const screenMat = new THREE.MeshStandardMaterial({
    color: 0x1a1d22,
    roughness: 0.45,
    metalness: 0.06,
    emissive: 0x000000,
  })
  applyDiffuseIblOnlyPolicy(bodyMat, usesDiffuseIblOnlyMaterialPatch())
  applyDiffuseIblOnlyPolicy(screenMat, usesDiffuseIblOnlyMaterialPatch())
  const screenMesh = new THREE.Mesh(screenGeo, screenMat)
  screenMesh.name = 'screen'
  screenMesh.position.z = d / 2 + 0.002
  group.add(screenMesh)
  ensureScreenTransitionPatchOnMesh(screenMesh)

  group.userData.bodyMesh = bodyMesh
  group.userData.screenMesh = screenMesh
  return group
}

/**
 * 为屏幕 mesh 加载或清除贴图 / 视频：无用户文件时保留 glTF 默认材质；仅在上传后替换。
 *
 * @param group - 图层 Group
 * @param layer - Pinia 图层数据
 * @param globalTimeSec - 解析多图栈时使用；缺省为 `store.currentTimeSeconds`（导出帧须显式传入）
 */
/**
 * 图层屏幕媒体相关字段的指纹，用作 `stopLayerWatch` 的稳定依赖键。
 * 显式纳入关键帧 → 屏幕图绑定（`kf.screenImageId`），避免 deep watch 隐式依赖丢失。
 */
function layerScreenFingerprint(l: AnimationLayer): string {
  const stackKey = l.screenImages.map((s) => `${s.id}:${s.textureSrc ?? ''}`).join(';')
  const kfImgKey = l.clips.map(clipKeyframeImageFingerprint).join('|')
  const trans = l.screenImageTransitionMode ?? ''
  return `${l.id}:${l.screenTextureSrc ?? ''}:${l.screenMediaType}:${stackKey}#${kfImgKey}@t:${trans}`
}

function clipKeyframeImageFingerprint(c: { keyframes: ReadonlyArray<{ id: string; screenImageId?: string | null }> }): string {
  return c.keyframes.map((k) => `${k.id}:${k.screenImageId ?? ''}`).join(',')
}

/**
 * GPU 过渡仅在「时间连续推进」的场景生效（自然播放 / 导出逐帧）；scrub、选中关键帧、编辑屏幕图等
 * 离散时间变化走瞬时切换，否则 `mixRatio` 推进依赖 `currentTimeSeconds` 变化，
 * 拖停后会卡在过渡中间帧，表现为「显示选中关键帧的旧图」。
 */
function getEffectiveScreenTransitionMode(layer: AnimationLayer): ScreenImageTransitionModeId {
  const isExporting = sceneCanvasExportDepth > 0
  const allowGpu = store.isPlaying || isExporting
  const base = resolvedLayerScreenImageTransitionMode(layer)
  return allowGpu ? base : SCREEN_IMAGE_TRANSITION_NONE
}

function applyScreenTextureToGroup(group: THREE.Group, layer: AnimationLayer, globalTimeSec?: number): void {
  const screenMesh = group.userData.screenMesh as THREE.Mesh | undefined
  if (!screenMesh) return

  ensureScreenMaterialSnapshot(group)

  const t = globalTimeSec ?? store.currentTimeSeconds
  let src: string | null = null
  let mediaType: 'none' | 'image' | 'video' = 'none'
  if (layer.screenMediaType === 'video' && layer.screenTextureSrc) {
    src = layer.screenTextureSrc
    mediaType = 'video'
  } else {
    const raster = effectiveScreenRasterObjectUrl(layer, t)
    if (raster) {
      src = raster
      mediaType = 'image'
    }
  }

  if (!src || mediaType === 'none') {
    const rid = ((group.userData.textureLoadId as number | undefined) ?? 0) + 1
    group.userData.textureLoadId = rid
    group.userData.lastAppliedRasterSrc = undefined
    disposeScreenRasterTransitionState(group)
    releaseUploadedScreenResources(group)
    restoreScreenMaterialSnapshot(group)
    return
  }

  /**
   * 设备屏视频：`applyLayerPosesAtTime`（含导出逐帧）会高频调用本函数；若与已建立的 blob URL 相同则
   * 必须复用同一 `<video>` + `VideoTexture`，否则每帧 `releaseUploadedScreenResources` 会拆掉解码器导致黑屏/导出错乱。
   */
  if (mediaType === 'video' && src) {
    const existingVideo = group.userData[SCREEN_USERDATA.screenVideoEl] as HTMLVideoElement | undefined
    const existingMap = group.userData[SCREEN_USERDATA.screenMap]
    if (
      group.userData.lastAppliedRasterSrc === src &&
      existingVideo &&
      (existingVideo.src === src || existingVideo.currentSrc === src)
    ) {
      if (existingMap instanceof THREE.VideoTexture && existingMap.image === existingVideo) {
        return
      }
      /** 已登记 URL 且 `<video>` 存在，但 `VideoTexture` 尚未挂上（`loadeddata` 未完成）：勿释放重开 */
      if (!(existingMap instanceof THREE.VideoTexture)) {
        return
      }
    }
  }

  if (mediaType === 'image' && src) {
    syncScreenRasterTextureWithTransition(group, layer, screenMesh, t, getEffectiveScreenTransitionMode(layer), {
      isImagePlane: false,
      imagePlaneOpacity: 1,
      releaseUserScreenTextures: releaseUploadedScreenResources,
      bindImagePlane: applyImagePlaneTextureToScreenMesh,
      bindDeviceScreen: bindMapAndEmissiveToScreenMesh,
      onImagePlaneRasterNaturalSize: () => {},
    })
    return
  }

  group.userData.lastAppliedRasterSrc = src

  const loadId = ((group.userData.textureLoadId as number | undefined) ?? 0) + 1
  group.userData.textureLoadId = loadId

  releaseUploadedScreenResources(group)

  const video = document.createElement('video')
  video.muted = true
  video.playsInline = true
  video.setAttribute('playsinline', '')
  video.preload = 'auto'
  video.src = src
  group.userData[SCREEN_USERDATA.screenVideoEl] = video

  const onVideoDecodeUnsupported = () => {
    store.setStubMessage(store.tUi('editorMessages.screenVideoWebCodecUnsupported'))
  }
  const onLoadedData = () => {
    runWhenVideoHasDrawableDimensions(
      video,
      () => {
        if (group.userData.textureLoadId !== loadId) return
        const sm = group.userData.screenMesh as THREE.Mesh | undefined
        if (!sm || sm !== screenMesh) return
        const vt = new THREE.VideoTexture(video)
        vt.colorSpace = THREE.SRGBColorSpace
        vt.minFilter = THREE.LinearFilter
        vt.magFilter = THREE.LinearFilter
        bindMapAndEmissiveToScreenMesh(sm, vt)
        group.userData[SCREEN_USERDATA.screenMap] = vt
        void syncScreenVideoForGroup(group, layer, t, false, 'scrub')
      },
      { onTimeout: onVideoDecodeUnsupported },
    )
  }
  /** `loadeddata` 后仍可能 `videoWidth===0`，须再等可绘制尺寸再建 `VideoTexture`，否则 WebGL `texImage2D: no video` */
  video.addEventListener('loadeddata', onLoadedData, { once: true })
  video.addEventListener('error', onVideoDecodeUnsupported, { once: true })
  try {
    video.load()
  } catch {
    /* ignore */
  }
}

/** 预览下视频与时间轴对齐策略：`export` 逐帧精确 seek；`scrub` 拖拽；`playback` 播放（宽松 seek + `play`） */
type VideoPreviewSyncKind = 'export' | 'scrub' | 'playback'

/**
 * 将图层视频贴图与全局时间轴对齐。
 *
 * @param group - 图层 Group
 * @param layer - 图层数据
 * @param globalTimeSec - 全局时间轴（秒）
 * @param waitSeek - 为 true 时 `await seeked`（导出）
 * @param preview - 预览策略（`waitSeek` 为 true 时忽略）
 */
async function syncScreenVideoForGroup(
  group: THREE.Group,
  layer: AnimationLayer,
  globalTimeSec: number,
  waitSeek: boolean,
  preview: VideoPreviewSyncKind = 'scrub',
): Promise<void> {
  const effective = !layer.screenTextureSrc ? 'none' : layer.screenMediaType
  if (effective !== 'video') return
  const video = group.userData[SCREEN_USERDATA.screenVideoEl] as HTMLVideoElement | undefined
  const tex = group.userData[SCREEN_USERDATA.screenMap] as THREE.Texture | undefined
  if (!video || !(tex instanceof THREE.VideoTexture)) return
  const t = resolveVideoTimeForLayer(layer, globalTimeSec)
  if (waitSeek) {
    try {
      video.pause()
    } catch {
      /* ignore */
    }
    await waitVideoSeeked(video, t)
  } else {
    const d = video.duration
    const clamped =
      Number.isFinite(d) && d > 0 ? Math.min(Math.max(0, t), Math.max(0, d - 1e-3)) : Math.max(0, t)
    const drift = Math.abs(video.currentTime - clamped)
    const thresh = preview === 'playback' ? 0.09 : 0.003
    if (drift > thresh) {
      if (preview === 'scrub') {
        /** 预览暂停前须等 seek 完成，否则 `VideoTexture` 常在解码前采样到黑帧 */
        await waitVideoSeeked(video, clamped)
      } else {
        try {
          video.currentTime = clamped
        } catch {
          /* ignore */
        }
      }
    }
    if (preview === 'playback') {
      void video.play().catch(() => {})
    } else {
      try {
        video.pause()
      } catch {
        /* ignore */
      }
    }
  }
  /** 尺寸为 0 时上传会触发 `texImage2D: no video` */
  if (video.videoWidth > 0 && video.videoHeight > 0) {
    tex.needsUpdate = true
  }
}

/**
 * 对所有含视频贴图的图层执行 seek。
 *
 * @param timeSeconds - 全局时间
 * @param waitSeek - 是否等待 `seeked`
 * @param preview - 预览策略
 */
async function syncAllScreenVideosAtTime(
  timeSeconds: number,
  waitSeek: boolean,
  preview: VideoPreviewSyncKind = 'scrub',
): Promise<void> {
  for (const layer of store.animationLayers) {
    const g = layerGroups.get(layer.id)
    if (g) await syncScreenVideoForGroup(g, layer, timeSeconds, waitSeek, waitSeek ? 'export' : preview)
  }
}

/**
 * 构建图片/视频平面 Group（无机身，仅一个 PlaneGeometry screen mesh）。
 * 使用 `MeshStandardMaterial` 以兼容 `bindMapAndEmissiveToScreenMesh`。
 * 创建时立即加载默认贴图 `Other/texture.jpg`。
 *
 * @param layerId - 图层 id
 */
function buildImagePlaneGroup(layerId: string): THREE.Group {
  const group = new THREE.Group()
  const geo = new THREE.PlaneGeometry(2, 2)
  const mat = new THREE.MeshStandardMaterial({
    color: 0x000000,
    roughness: 1,
    metalness: 1,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 1,
    depthWrite: true,
  })
  applyDiffuseIblOnlyPolicy(mat, usesDiffuseIblOnlyMaterialPatch())
  const mesh = new THREE.Mesh(geo, mat)
  mesh.name = 'screen'
  registerLayerOwnership(mesh, layerId)
  group.add(mesh)
  ensureScreenTransitionPatchOnMesh(mesh)
  group.userData.screenMesh = mesh
  group.userData.bodyMesh = undefined

  /** 异步加载默认贴图；若用户已上传贴图则由外层逻辑覆盖 */
  const loader = new THREE.TextureLoader()
  loader.load(defaultImagePlaneTextureUrl, (tex) => {
    const sm = group.userData.screenMesh as THREE.Mesh | undefined
    if (!sm) { tex.dispose(); return }
    if (group.userData.hasUserTexture) { tex.dispose(); return }
    tex.colorSpace = THREE.SRGBColorSpace
    tex.needsUpdate = true
    applyImagePlaneTextureToScreenMesh(sm, tex)
    group.userData.defaultTexture = tex
    const img = new Image()
    img.onload = () => { resizeImagePlaneToAspect(group, img.naturalWidth, img.naturalHeight) }
    img.src = defaultImagePlaneTextureUrl
  })

  return group
}

/**
 * image-plane 专用贴图绑定：使用自发光贴图表现屏幕内容（避免受漫反射/环境光影响）。
 *
 * @param screenMesh - 屏幕网格
 * @param tex - 已配置色彩空间的纹理
 */
function applyImagePlaneTextureToScreenMesh(screenMesh: THREE.Mesh, tex: THREE.Texture): void {
  const mats: THREE.Material[] = Array.isArray(screenMesh.material) ? screenMesh.material : [screenMesh.material]
  for (const m of mats) {
    if (m instanceof THREE.MeshStandardMaterial) {
      m.map = null
      m.emissiveMap = tex
      m.color.setHex(0x000000)
      m.emissive.setHex(0xffffff)
      m.emissiveIntensity = 1.5
      m.roughness = 1
      m.metalness = 1
      m.needsUpdate = true
    }
  }
}

/**
 * 将插值得到的材质不透明度应用到图片/视频平面屏幕材质（与导出帧共用）。
 *
 * @param group - 图层 Group
 * @param opacity - 0～1
 */
function applyImagePlaneScreenOpacity(group: THREE.Group, opacity: number): void {
  const o = clampMaterialOpacity(opacity)
  const sm = group.userData.screenMesh as THREE.Mesh | undefined
  if (!sm) return
  const mats: THREE.Material[] = Array.isArray(sm.material) ? sm.material : [sm.material]
  for (const raw of mats) {
    if (raw instanceof THREE.MeshStandardMaterial) {
      raw.transparent = o < 1 - 1e-6
      raw.opacity = o
      raw.depthWrite = o >= 1 - 1e-6
      raw.needsUpdate = true
    }
  }
}

/**
 * 为 image-plane 图层在贴图加载成功后调整 PlaneGeometry 宽高比，
 * 使 plane 与图片/视频像素比例一致，且长边不超过 3 个单位。
 *
 * @param group - 图层 Group
 * @param naturalW - 图片原始像素宽
 * @param naturalH - 图片原始像素高
 */
function resizeImagePlaneToAspect(group: THREE.Group, naturalW: number, naturalH: number): void {
  const screenMesh = group.userData.screenMesh as THREE.Mesh | undefined
  if (!screenMesh || !(screenMesh.geometry instanceof THREE.PlaneGeometry)) return
  if (naturalW <= 0 || naturalH <= 0) return
  const MAX_SIDE = 3.0
  const MIN_SIDE = 0.3
  const ar = naturalW / naturalH
  let w: number
  let h: number
  if (ar >= 1) {
    w = Math.min(MAX_SIDE, Math.max(MIN_SIDE, 2.0))
    h = w / ar
  } else {
    h = Math.min(MAX_SIDE, Math.max(MIN_SIDE, 2.2))
    w = h * ar
  }
  screenMesh.geometry.dispose()
  screenMesh.geometry = new THREE.PlaneGeometry(w, h)
}

/**
 * 绘制文字到离屏 Canvas 并返回 CanvasTexture（宽 1024，高自适应）。
 * 字体固定为系统无衬线字体，支持中英文。
 *
 * @param text - 展示文字
 * @param fontSize - 字号（逻辑 px，内部乘以 DPR 系数渲染）
 * @param color - CSS 颜色（默认 `'#888888'`）
 */
function renderTextToCanvasTexture(
  text: string,
  fontSize: number,
  color: string = '#888888',
  fontWeight: 'normal' | 'bold' = 'bold',
  textAlign: 'left' | 'center' | 'right' = 'center',
): { texture: THREE.CanvasTexture; aspect: number } {
  const CANVAS_W = 1024
  const scale = 2
  const fs = Math.max(12, Math.min(300, fontSize)) * scale
  const fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  const font = `${fontWeight} ${fs}px ${fontFamily}`
  const content = text.length > 0 ? text : 'Animock'
  const lines = content.split('\n')
  const lineHeight = Math.ceil(fs * 1.25)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  ctx.font = font
  const textW = Math.ceil(
    Math.max(
      ...lines.map((line) => ctx.measureText(line.length > 0 ? line : ' ').width),
      ctx.measureText(' ').width,
    ),
  )
  const textH = Math.max(lineHeight, lineHeight * lines.length)
  const pad = Math.ceil(fs * 0.3)
  canvas.width = Math.max(CANVAS_W, textW + pad * 2)
  canvas.height = textH + pad * 2
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.font = font
  ctx.fillStyle = color || '#888888'
  ctx.textBaseline = 'alphabetic'
  ctx.textAlign = textAlign
  const startY = canvas.height / 2 - textH / 2 + lineHeight * 0.85
  const drawX =
    textAlign === 'left' ? pad : textAlign === 'right' ? canvas.width - pad : canvas.width / 2
  lines.forEach((line, index) => {
    ctx.fillText(line, drawX, startY + index * lineHeight)
  })
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.needsUpdate = true
  return { texture, aspect: canvas.width / canvas.height }
}

/**
 * 构建文字平面 Group，使用 Canvas 渲染文字作为贴图。
 *
 * @param layerId - 图层 id
 * @param textContent - 显示文字，默认 `'Animock'`
 * @param textFontSize - 字号，默认 `64`
 * @param textColor - 文字颜色，默认 `'#888888'`
 */
function buildTextPlaneGroup(
  layerId: string,
  textContent: string,
  textFontSize: number,
  textColor: string = '#888888',
  textFontWeight: 'normal' | 'bold' = 'bold',
  textAlign: 'left' | 'center' | 'right' = 'center',
): THREE.Group {
  const group = new THREE.Group()
  const { texture, aspect } = renderTextToCanvasTexture(
    textContent,
    textFontSize,
    textColor,
    textFontWeight,
    textAlign,
  )
  const MAX_W = 3.0
  const w = MAX_W
  const h = w / aspect
  const geo = new THREE.PlaneGeometry(w, Math.max(0.05, h))
  const mat = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    side: THREE.DoubleSide,
    depthWrite: false,
    alphaTest: 0.005,
  })
  applyDiffuseIblOnlyPolicy(mat, usesDiffuseIblOnlyMaterialPatch())
  const mesh = new THREE.Mesh(geo, mat)
  mesh.name = 'screen'
  registerLayerOwnership(mesh, layerId)
  group.add(mesh)
  group.userData.screenMesh = mesh
  group.userData.bodyMesh = undefined
  group.userData.textCanvasTexture = texture
  return group
}

/**
 * 更新文字平面的 Canvas 贴图（文字内容、字号或颜色变化时调用）。
 *
 * @param group - 图层 Group
 * @param textContent - 新文字
 * @param textFontSize - 新字号
 * @param textColor - 新颜色
 */
function updateTextPlaneTexture(
  group: THREE.Group,
  textContent: string,
  textFontSize: number,
  textColor: string = '#888888',
  textFontWeight: 'normal' | 'bold' = 'bold',
  textAlign: 'left' | 'center' | 'right' = 'center',
): void {
  const screenMesh = group.userData.screenMesh as THREE.Mesh | undefined
  if (!screenMesh) return
  const oldTex = group.userData.textCanvasTexture as THREE.CanvasTexture | undefined
  if (oldTex) oldTex.dispose()
  const { texture, aspect } = renderTextToCanvasTexture(
    textContent,
    textFontSize,
    textColor,
    textFontWeight,
    textAlign,
  )
  group.userData.textCanvasTexture = texture
  const MAX_W = 3.0
  const w = MAX_W
  const h = w / aspect
  if (screenMesh.geometry instanceof THREE.PlaneGeometry) screenMesh.geometry.dispose()
  screenMesh.geometry = new THREE.PlaneGeometry(w, Math.max(0.05, h))
  const mat = screenMesh.material as THREE.MeshBasicMaterial
  if (mat instanceof THREE.MeshBasicMaterial) {
    mat.map = texture
    mat.needsUpdate = true
  }
}

function applyTextPlaneScreenOpacity(group: THREE.Group, opacity: number): void {
  const o = clampMaterialOpacity(opacity)
  const sm = group.userData.screenMesh as THREE.Mesh | undefined
  if (!sm) return
  const mats: THREE.Material[] = Array.isArray(sm.material) ? sm.material : [sm.material]
  for (const raw of mats) {
    if (raw instanceof THREE.MeshBasicMaterial) {
      raw.transparent = true
      raw.opacity = o
      raw.depthWrite = o >= 1 - 1e-6
      raw.needsUpdate = true
    }
  }
}

/** 清除图片平面用户贴图并恢复内置默认纹理（幂等：已有默认贴图时同步复用，避免每帧重载闪烁） */
function restoreImagePlaneDefaultUserTexture(group: THREE.Group): void {
  group.userData.hasUserTexture = false
  disposeScreenRasterTransitionState(group)
  releaseUploadedScreenResources(group)

  const sm = group.userData.screenMesh as THREE.Mesh | undefined
  if (!sm) return

  const existingDefault = group.userData.defaultTexture as THREE.Texture | undefined
  if (existingDefault) {
    applyImagePlaneTextureToScreenMesh(sm, existingDefault)
    return
  }

  const loader = new THREE.TextureLoader()
  loader.load(defaultImagePlaneTextureUrl, (tex) => {
    if (group.userData.hasUserTexture) {
      tex.dispose()
      return
    }
    const m = sm.material
    if (!(m instanceof THREE.MeshStandardMaterial)) {
      tex.dispose()
      return
    }
    tex.colorSpace = THREE.SRGBColorSpace
    tex.needsUpdate = true
    applyImagePlaneTextureToScreenMesh(sm, tex)
    group.userData.defaultTexture = tex
    const img = new Image()
    img.onload = () => {
      resizeImagePlaneToAspect(group, img.naturalWidth, img.naturalHeight)
    }
    img.src = defaultImagePlaneTextureUrl
  })
}

/**
 * 按全局时间同步图片平面栅格贴图（多图栈 / 单图）。
 *
 * @param group - 图层 Group
 * @param layer - 图层数据
 * @param globalTimeSec - 全局时间（秒）
 */
function syncImagePlaneRasterAtTime(group: THREE.Group, layer: AnimationLayer, globalTimeSec: number): void {
  if (layer.screenMediaType === 'video') return
  const raster = effectiveScreenRasterObjectUrl(layer, globalTimeSec)
  const screenMesh = group.userData.screenMesh as THREE.Mesh | undefined
  if (!screenMesh) return
  if (layer.screenMediaType === 'image' && raster) {
    const pose = evaluateLayerAtTime(layer, globalTimeSec)
    const opacity = pose.materialOpacity ?? 1
    syncScreenRasterTextureWithTransition(group, layer, screenMesh, globalTimeSec, getEffectiveScreenTransitionMode(layer), {
      isImagePlane: true,
      imagePlaneOpacity: opacity,
      releaseUserScreenTextures: releaseUploadedScreenResources,
      bindImagePlane: applyImagePlaneTextureToScreenMesh,
      bindDeviceScreen: bindMapAndEmissiveToScreenMesh,
      onImagePlaneRasterNaturalSize: (g, srcUrl) => {
        const img = new Image()
        img.onload = () => resizeImagePlaneToAspect(g, img.naturalWidth, img.naturalHeight)
        img.src = srcUrl
      },
    })
  } else {
    /** 无用户贴图时保持 `buildImagePlaneGroup` 的默认纹理；勿每帧清空 emissiveMap 否则黑屏闪烁 */
    const hadUserContent = Boolean(
      group.userData.hasUserTexture ||
        group.userData[SCREEN_USERDATA.screenMap] ||
        group.userData[SCREEN_USERDATA.screenVideoEl],
    )
    if (!hadUserContent) return
    disposeScreenRasterTransitionState(group)
    group.userData.appliedTextureKey = undefined
    restoreImagePlaneDefaultUserTexture(group)
  }
}

/**
 * 同步图层列表与机型模板：增删、模板变更时重建 mesh；贴图在结构稳定后单独应用。
 */
function syncMockupLayerStructure(): void {
  if (!layersRoot) return
  const layers = store.animationLayers
  const wanted = new Set(layers.map((l) => l.id))

  for (const id of [...layerGroups.keys()]) {
    if (!wanted.has(id)) {
      const g = layerGroups.get(id)
      if (g) {
        const box = layerSelectionBoxes.get(id)
        if (box) {
          layersRoot.remove(box)
          box.geometry.dispose()
          ;(box.material as THREE.Material).dispose()
          layerSelectionBoxes.delete(id)
        }
        layersRoot.remove(g)
        disposeGroupContents(g)
      }
      layerGroups.delete(id)
    }
  }

  for (const layer of layers) {
    let group = layerGroups.get(layer.id)
    if (!group) {
      group = new THREE.Group()
      group.userData.layerId = layer.id
      group.visible = false
      registerLayerOwnership(group, layer.id)
      group.userData.deviceTemplateId = ''
      layerGroups.set(layer.id, group)
      applyLayerFrameToGroup(group, layer, store.currentTimeSeconds)
      layersRoot.add(group)
    }
    group.userData.layerId = layer.id
    registerLayerOwnership(group, layer.id)

    const tid = layer.deviceTemplateId
    if (group.userData.deviceTemplateId !== tid) {
      disposeGroupContents(group)
      group.userData.deviceTemplateId = tid
      group.userData.modelLoadId = (group.userData.modelLoadId ?? 0) + 1
      const loadAttempt = group.userData.modelLoadId as number

      if (tid === 'image-plane') {
        /** 图片/视频平面：纯 PlaneGeometry，贴图由用户上传 */
        const imgGroup = buildImagePlaneGroup(layer.id)
        while (imgGroup.children.length > 0) {
          const ch = imgGroup.children[0]!
          group.add(ch)
        }
        group.userData.bodyMesh = imgGroup.userData.bodyMesh
        group.userData.screenMesh = imgGroup.userData.screenMesh
        ensureScreenMaterialSnapshot(group)
      } else if (tid === 'text-plane') {
        /** 文字平面：Canvas 纹理，内容由 layer.textContent/textFontSize/textColor 驱动 */
        const txtGroup = buildTextPlaneGroup(
          layer.id,
          layer.textContent ?? 'Animock',
          layer.textFontSize ?? 64,
          layer.textColor ?? '#888888',
          layer.textFontWeight ?? 'bold',
          layer.textAlign ?? 'center',
        )
        while (txtGroup.children.length > 0) {
          const ch = txtGroup.children[0]!
          group.add(ch)
        }
        group.userData.bodyMesh = txtGroup.userData.bodyMesh
        group.userData.screenMesh = txtGroup.userData.screenMesh
        group.userData.textCanvasTexture = txtGroup.userData.textCanvasTexture
        group.userData.appliedTextKey = `${layer.textContent ?? ''}\0${layer.textFontSize ?? 64}\0${layer.textColor ?? '#888888'}\0${layer.textFontWeight ?? 'bold'}\0${layer.textAlign ?? 'center'}`
      } else {
        const template = MOCKUP_DEVICE_TEMPLATES.find((t) => t.id === tid)
        if (template?.modelUrl) {
          group.userData.bodyMesh = undefined
          group.userData.screenMesh = undefined
          loadGltfModelIntoGroup(group, template, loadAttempt, layer)
        } else {
          const phone = buildPhoneGroup(tid)
          while (phone.children.length > 0) {
            const ch = phone.children[0]!
            registerLayerOwnership(ch, layer.id)
            group.add(ch)
          }
          group.userData.bodyMesh = phone.userData.bodyMesh
          group.userData.screenMesh = phone.userData.screenMesh
          ensureScreenMaterialSnapshot(group)
        }
      }
      group.userData.appliedTextureKey = undefined
    }

    if (tid === 'text-plane') {
      /** 文字图层：内容、字号或颜色变化时重新绘制 Canvas */
      const textKey = `${layer.textContent ?? ''}\0${layer.textFontSize ?? 64}\0${layer.textColor ?? '#888888'}\0${layer.textFontWeight ?? 'bold'}\0${layer.textAlign ?? 'center'}`
      if (group.userData.appliedTextKey !== textKey) {
        group.userData.appliedTextKey = textKey
        updateTextPlaneTexture(
          group,
          layer.textContent ?? 'Animock',
          layer.textFontSize ?? 64,
          layer.textColor ?? '#888888',
          layer.textFontWeight ?? 'bold',
          layer.textAlign ?? 'center',
        )
      }
    } else if (tid === 'image-plane') {
      /** 图片/视频平面：视频走独立分支；栅格图（含多图栈）由 {@link syncImagePlaneRasterAtTime} 处理 */
      if (layer.screenMediaType === 'video' && layer.screenTextureSrc) {
        const texKey = `${layer.screenTextureSrc}\0video`
        if (group.userData.appliedTextureKey !== texKey) {
          group.userData.appliedTextureKey = texKey
          const src = layer.screenTextureSrc
          const loadId = ((group.userData.textureLoadId as number | undefined) ?? 0) + 1
          group.userData.textureLoadId = loadId
          group.userData.hasUserTexture = true
          releaseUploadedScreenResources(group)
          const defTex = group.userData.defaultTexture as THREE.Texture | undefined
          if (defTex) {
            defTex.dispose()
            group.userData.defaultTexture = undefined
          }
          const video = document.createElement('video')
          video.muted = true
          video.playsInline = true
          video.setAttribute('playsinline', '')
          video.preload = 'auto'
          video.src = src
          group.userData[SCREEN_USERDATA.screenVideoEl] = video
          const onVideoDecodeUnsupported = () => {
            store.setStubMessage(store.tUi('editorMessages.screenVideoWebCodecUnsupported'))
          }
          const onLoadedData = () => {
            runWhenVideoHasDrawableDimensions(
              video,
              () => {
                if (group.userData.textureLoadId !== loadId) return
                const sm = group.userData.screenMesh as THREE.Mesh | undefined
                if (!sm) return
                const vt = new THREE.VideoTexture(video)
                vt.colorSpace = THREE.SRGBColorSpace
                vt.minFilter = THREE.LinearFilter
                vt.magFilter = THREE.LinearFilter
                applyImagePlaneTextureToScreenMesh(sm, vt)
                group.userData[SCREEN_USERDATA.screenMap] = vt
                resizeImagePlaneToAspect(group, video.videoWidth, video.videoHeight)
                void syncScreenVideoForGroup(group, layer, store.currentTimeSeconds, false, 'scrub')
              },
              { onTimeout: onVideoDecodeUnsupported },
            )
          }
          video.addEventListener('loadeddata', onLoadedData, { once: true })
          video.addEventListener('error', onVideoDecodeUnsupported, { once: true })
          try {
            video.load()
          } catch {
            /* ignore */
          }
        }
      } else {
        syncImagePlaneRasterAtTime(group, layer, store.currentTimeSeconds)
      }
    } else {
      const raster = effectiveScreenRasterObjectUrl(layer, store.currentTimeSeconds)
      const rid =
        layer.screenMediaType === 'image' && layer.screenImages.length > 0
          ? resolveScreenImageIdAtGlobalTime(layer, store.currentTimeSeconds)
          : ''
      const texKey = `${layer.screenTextureSrc ?? ''}\0${raster ?? ''}\0${layer.screenMediaType}\0${rid}`
      if (group.userData.appliedTextureKey !== texKey) {
        group.userData.appliedTextureKey = texKey
        applyScreenTextureToGroup(group, layer, store.currentTimeSeconds)
      }
    }

    applySelectionBox(layer.id, group, isLayerVisuallySelected(layer.id))
  }
}

/**
 * 按导出质量预设调整 WebGLRenderer（当前映射：high/best 开启阴影贴图）。
 *
 * @param q - 导出质量
 */
function applyExportRendererQuality(q: ExportRenderQualityId): void {
  if (!renderer) return
  savedShadowMapEnabled = renderer.shadowMap.enabled
  renderer.shadowMap.enabled = q === 'high' || q === 'best'
}

/** 恢复 `applyExportRendererQuality` 修改的渲染器状态 */
function restoreExportRendererQuality(): void {
  if (!renderer || savedShadowMapEnabled === null) return
  renderer.shadowMap.enabled = savedShadowMapEnabled
  savedShadowMapEnabled = null
}

/**
 * 开始导出视觉会话（选中高亮、渲染质量）。
 *
 * @param opts - 与预览分离的视觉选项
 */
function beginExportVisualSession(opts: ExportVisualSessionOptions): void {
  exportVisualDepth += 1
  if (exportVisualDepth === 1) {
    suppressEditorOverlayForExport = opts.suppressEditorOverlay
    applyExportRendererQuality(opts.quality)
  }
  for (const layer of store.animationLayers) {
    const group = layerGroups.get(layer.id)
    if (!group) continue
    applySelectionBox(layer.id, group, isLayerVisuallySelected(layer.id))
  }
}

/** 结束导出视觉会话，恢复编辑器视觉与渲染质量 */
function endExportVisualSession(): void {
  if (exportVisualDepth <= 0) return
  exportVisualDepth -= 1
  if (exportVisualDepth === 0) {
    suppressEditorOverlayForExport = false
    restoreExportRendererQuality()
  }
  for (const layer of store.animationLayers) {
    const group = layerGroups.get(layer.id)
    if (!group) continue
    applySelectionBox(layer.id, group, isLayerVisuallySelected(layer.id))
  }
}

/**
 * 按全局时间对每层求值并写入 Group 变换（与导出 `renderFrameAtTime` 共用逻辑）。
 *
 * 姿态与可见性来自同一个纯时间快照。预览、拖动播放头和导出都不依赖上一帧，
 * 也不把运行时可见性写回 reactive 图层，避免每帧触发深度 watcher。
 *
 * @param timeSeconds - 时间轴时刻（秒）
 */
function applyLayerFrameToGroup(
  group: THREE.Group,
  layer: AnimationLayer,
  timeSeconds: number,
): void {
  const frame = evaluateLayerFrameAtGlobalTime(layer, timeSeconds)
  const pose = frame.pose
  group.position.set(pose.position.x, pose.position.y, pose.position.z)
  group.rotation.set(
    THREE.MathUtils.degToRad(pose.rotationDeg.x),
    THREE.MathUtils.degToRad(pose.rotationDeg.y),
    THREE.MathUtils.degToRad(pose.rotationDeg.z),
  )
  group.scale.set(pose.scale.x, pose.scale.y, pose.scale.z)

  const wasVisible = group.visible
  if (!wasVisible && frame.visible) {
    /** Commit the evaluated transform before the model becomes renderable. */
    group.updateMatrix()
    group.updateWorldMatrix(true, true)
  }
  group.visible = frame.visible
  if (layer.deviceTemplateId === 'image-plane') {
    applyImagePlaneScreenOpacity(group, pose.materialOpacity)
  } else if (layer.deviceTemplateId === 'text-plane') {
    applyTextPlaneScreenOpacity(group, pose.materialOpacity)
  }
}

function applyLayerPosesAtTime(
  timeSeconds: number,
): void {
  const t = Number(timeSeconds)
  for (const layer of store.animationLayers) {
    const group = layerGroups.get(layer.id)
    if (!group) continue
    applyLayerFrameToGroup(group, layer, t)
  }

  for (const layer of store.animationLayers) {
    const g = layerGroups.get(layer.id)
    if (!g) continue
    if (layer.deviceTemplateId === 'image-plane') {
      if (layer.screenMediaType !== 'video') {
        syncImagePlaneRasterAtTime(g, layer, t)
      }
    } else if (layer.deviceTemplateId !== 'text-plane') {
      applyScreenTextureToGroup(g, layer, t)
    }
  }
}

/**
 * 渲染一帧：按时间应用位姿、同步视频贴图并 `render`（背景由 store 决定，不在此重复创建渐变纹理）。
 *
 * @param timeSeconds - 时间轴时刻（秒）
 */
async function renderFrameAtTime(timeSeconds: number): Promise<void> {
  applyLayerPosesAtTime(timeSeconds)
  await syncAllScreenVideosAtTime(timeSeconds, true, 'export')
  syncLayerSelectionBoxesForRender()
  enforceFixedCamera()
  if (renderer && scene && camera) renderer.render(scene, camera)
}

function tick(): void {
  raf = window.requestAnimationFrame(tick)
  if (sceneCanvasExportDepth > 0) return
  /** Every preview draw is based on one complete absolute-time scene snapshot. */
  applyLayerPosesAtTime(store.currentTimeSeconds)
  if (store.isPlaying) {
    void syncAllScreenVideosAtTime(store.currentTimeSeconds, false, 'playback')
  }
  syncLayerSelectionBoxesForRender()
  enforceFixedCamera()
  if (renderer && scene && camera) renderer.render(scene, camera)
}

function resize(): void {
  if (sceneCanvasExportDepth > 0) return
  if (!host.value || !renderer || !camera) return
  const w = Math.max(1, host.value.clientWidth)
  const h = Math.max(1, host.value.clientHeight)
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  renderer.setSize(w, h, false)
  syncCameraProjectionForSize(w, h)
  refreshBackgroundImageCover()
}

/**
 * 将绘制缓冲设为场景导出尺寸（`devicePixelRatio` 置 1 以保证帧像素与宽高一致）。
 *
 * @param width - 像素宽
 * @param height - 像素高
 */
function beginSceneCanvasSize(width: number, height: number): void {
  if (!renderer || !camera) return
  const w = Math.max(1, Math.round(width))
  const h = Math.max(1, Math.round(height))
  sceneCanvasExportDepth += 1
  renderer.setPixelRatio(1)
  renderer.setSize(w, h, false)
  syncCameraProjectionForSize(w, h)
  refreshBackgroundImageCover()
}

/**
 * 结束场景导出尺寸，恢复视口自适应。
 */
function endSceneCanvasSize(): void {
  if (sceneCanvasExportDepth <= 0) return
  sceneCanvasExportDepth -= 1
  if (sceneCanvasExportDepth === 0) resize()
}

let ro: ResizeObserver | null = null
/** `ResizeObserver` 合并到下一帧，避免分割条拖动时每事件触发一次昂贵 `setSize`。 */
let resizeRafId = 0
/** 场景像素导出嵌套深度；>0 时跳过交互 `tick` 渲染与常规 `resize`。 */
let sceneCanvasExportDepth = 0

onMounted(() => {
  if (!host.value) return

  scene = new THREE.Scene()
  const halfH = ORTHO_VIEW_HEIGHT / 2
  camera = new THREE.OrthographicCamera(-halfH, halfH, halfH, -halfH, 0.01, 600)
  enforceFixedCamera()

  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
  renderer.outputColorSpace = THREE.SRGBColorSpace
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  host.value.appendChild(renderer.domElement)
  renderer.domElement.style.width = '100%'
  renderer.domElement.style.height = '100%'
  renderer.domElement.style.display = 'block'

  const initialLightDef = getLightingPresetDefinition(store.lightingPresetId)
  const initialHemiIntensity =
    initialLightDef.id === 'classic'
      ? CLASSIC_EDITOR_FILL_LIGHT_INTENSITY
      : resolvedHemisphereIntensity(initialLightDef) * store.viewportAmbientIntensityScale
  const initialDirIntensity =
    initialLightDef.id === 'classic'
      ? CLASSIC_EDITOR_FILL_LIGHT_INTENSITY
      : resolvedDirectionalIntensity(initialLightDef)
  hemiLight = new THREE.HemisphereLight(
    CLASSIC_EDITOR_HEMISPHERE_SKY,
    CLASSIC_EDITOR_HEMISPHERE_GROUND,
    initialHemiIntensity,
  )
  scene.add(hemiLight)
  dirLight = new THREE.DirectionalLight(0xffffff, initialDirIntensity)
  dirLight.position.set(
    initialLightDef.directionalPosition.x,
    initialLightDef.directionalPosition.y,
    initialLightDef.directionalPosition.z,
  )
  scene.add(dirLight)

  pmremGenerator = new THREE.PMREMGenerator(renderer)
  pmremGenerator.compileEquirectangularShader()

  layersRoot = new THREE.Group()
  scene.add(layersRoot)

  applyBackground()
  resize()

  setViewportExportApi({
    renderFrameAtTime,
    getCanvas: () => renderer?.domElement ?? null,
    beginSceneCanvasSize,
    endSceneCanvasSize,
    beginExportVisualSession,
    endExportVisualSession,
  })

  ro = new ResizeObserver(() => {
    if (resizeRafId) return
    resizeRafId = window.requestAnimationFrame(() => {
      resizeRafId = 0
      resize()
    })
  })
  ro.observe(host.value)

  stopBgWatch = watch(
    () => ({
      mode: store.backgroundMode,
      hex: store.backgroundHex,
      gradKey: store.sortedGradientStops.map((s) => `${s.id}:${s.hex}:${s.position}`).join('|'),
      imageSrc: store.backgroundImageSrc,
    }),
    applyBackground,
  )

  stopLightingWatch = watch(
    () => [store.lightingPresetId, store.viewportAmbientIntensityScale] as const,
    () => {
      syncLightingFromStore()
    },
    { immediate: true },
  )

  stopLayerWatch = watch(
    () => ({
      layers: store.animationLayers,
      sel: store.selectedLayerId,
      tex: store.animationLayers.map(layerScreenFingerprint).join('|'),
    }),
    () => {
      /**
       * 导出期间跳过此 watcher 的副作用：
       *  - `applyLayerPosesAtTime(store.currentTimeSeconds)` 会用 stale 时间重算姿态/可见性；
       *  - `syncAllScreenVideosAtTime(store.currentTimeSeconds, ...)` 会发出错误的 video seek，
       *    与离线管线 `renderFrameAtTime` 的 `await waitVideoSeeked` 串台，导致多层视频帧错乱。
       * 离线管线本身在每帧都会显式调用 `renderFrameAtTime(desc.timeSeconds)` 做正确同步。
       */
      if (sceneCanvasExportDepth > 0) return
      syncMockupLayerStructure()
      applyLayerPosesAtTime(store.currentTimeSeconds)
      void syncAllScreenVideosAtTime(
        store.currentTimeSeconds,
        false,
        store.isPlaying ? 'playback' : 'scrub',
      )
    },
    { deep: true, immediate: true },
  )

  stopTimeWatch = watch(
    () => [store.currentTimeSeconds, store.isPlaying] as const,
    ([sec]) => {
      /** During playback `tick` applies the pose synchronously immediately before `renderer.render`. */
      if (store.isPlaying) return
      applyLayerPosesAtTime(sec)
      void syncAllScreenVideosAtTime(sec, false, 'scrub')
    },
    { immediate: true },
  )

  stopTransformToolWatch = watch(
    () => store.viewportTransformTool,
    () => {
      if (store.viewportTransformTool === null && layerDrag) {
        try {
          layerDrag.el.releasePointerCapture(layerDrag.pointerId)
        } catch {
          /* 可能已释放 */
        }
        layerDrag = null
      }
      syncCanvasCursorFromTool()
    },
    { immediate: true },
  )

  stopClearToolWhenNoLayerWatch = watch(
    () => store.selectedLayerId,
    (id) => {
      if (!id) store.setViewportTransformTool(null)
    },
  )

  const canvasEl = renderer.domElement
  canvasEl.addEventListener('dragenter', onViewportHostDragEnter)
  canvasEl.addEventListener('dragleave', onViewportHostDragLeave)
  canvasEl.addEventListener('dragover', onViewportHostDragOver)
  canvasEl.addEventListener('drop', onViewportHostDrop)
  canvasEl.addEventListener('pointerenter', onViewportPointerEnter)
  canvasEl.addEventListener('pointerleave', onViewportPointerLeave)
  canvasEl.addEventListener('pointerdown', onLayerToolPointerDown)
  canvasEl.addEventListener('pointermove', onLayerToolPointerMove)
  canvasEl.addEventListener('pointerup', onLayerToolPointerUp)
  canvasEl.addEventListener('pointercancel', onLayerToolPointerUp)
  canvasEl.addEventListener('dblclick', onCanvasDoubleClickSelectLayer)
  window.addEventListener('keydown', onViewportTransformEscape)
  window.addEventListener('keydown', onViewportToolHotkeys)

  /**
   * 相机与场景视角固定：缩放/重置/自转均为空操作（保留 {@link ViewportHandlers} 以免旧调用报错）。
   */
  const handlers: ViewportHandlers = {
    zoomIn() {},
    zoomOut() {},
    resetView() {},
    toggleAutoRotate() {},
  }
  store.setViewportHandlers(handlers)

  // Ensure the very first frame uses evaluated timeline pose/visibility, not Group defaults.
  applyLayerPosesAtTime(store.currentTimeSeconds)
  syncLayerSelectionBoxesForRender()
  enforceFixedCamera()
  if (renderer && scene && camera) renderer.render(scene, camera)

  tick()
})

onUnmounted(() => {
  disposeSharedGltfDecoders()
  window.cancelAnimationFrame(raf)
  if (resizeRafId) window.cancelAnimationFrame(resizeRafId)
  resizeRafId = 0
  stopBgWatch?.()
  stopBgWatch = null
  stopLightingWatch?.()
  stopLightingWatch = null
  stopLayerWatch?.()
  stopLayerWatch = null
  stopTimeWatch?.()
  stopTimeWatch = null
  stopTransformToolWatch?.()
  stopTransformToolWatch = null
  stopClearToolWhenNoLayerWatch?.()
  stopClearToolWhenNoLayerWatch = null
  store.setViewportTransformTool(null)

  fileDragHoverDepth = 0
  host.value?.classList.remove('fileDropHover')

  if (renderer) {
    const c = renderer.domElement
    c.removeEventListener('dragenter', onViewportHostDragEnter)
    c.removeEventListener('dragleave', onViewportHostDragLeave)
    c.removeEventListener('dragover', onViewportHostDragOver)
    c.removeEventListener('drop', onViewportHostDrop)
    c.removeEventListener('pointerenter', onViewportPointerEnter)
    c.removeEventListener('pointerleave', onViewportPointerLeave)
    c.removeEventListener('pointerdown', onLayerToolPointerDown)
    c.removeEventListener('pointermove', onLayerToolPointerMove)
    c.removeEventListener('pointerup', onLayerToolPointerUp)
    c.removeEventListener('pointercancel', onLayerToolPointerUp)
    c.removeEventListener('dblclick', onCanvasDoubleClickSelectLayer)
    c.style.cursor = ''
  }
  layerDrag = null
  isViewportPointerInside = false
  window.removeEventListener('keydown', onViewportTransformEscape)
  window.removeEventListener('keydown', onViewportToolHotkeys)

  ro?.disconnect()
  ro = null

  setViewportExportApi(null)

  disposeBgTexture()

  disposeSceneEnvironment()
  pmremGenerator?.dispose()
  pmremGenerator = null
  hemiLight = null
  dirLight = null

  store.setViewportHandlers(null)

  if (layersRoot) {
    for (const box of layerSelectionBoxes.values()) {
      layersRoot.remove(box)
      box.geometry.dispose()
      ;(box.material as THREE.Material).dispose()
    }
    layerSelectionBoxes.clear()
    for (const id of [...layerGroups.keys()]) {
      const g = layerGroups.get(id)
      if (g) {
        layersRoot.remove(g)
        disposeGroupContents(g)
      }
    }
    layerGroups.clear()
    layersRoot = null
  }

  if (renderer) {
    renderer.dispose()
    const el = renderer.domElement
    el.parentElement?.removeChild(el)
  }
  renderer = null
  scene = null
  camera = null
})
</script>

<template>
  <div ref="host" class="host" :aria-label="store.tUi('viewport.ariaLabel')" />
</template>

<style scoped>
.host.fileDropHover {
  outline: 2px dashed rgba(37, 99, 235, 0.85);
  outline-offset: -2px;
}

.host {
  position: absolute;
  inset: 0;
  z-index: 0;
  min-height: 0;
  background:
    linear-gradient(45deg, #f3f3f5 25%, transparent 25%),
    linear-gradient(-45deg, #f3f3f5 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #f3f3f5 75%),
    linear-gradient(-45deg, transparent 75%, #f3f3f5 75%);
  background-size: 14px 14px;
  background-position:
    0 0,
    0 7px,
    7px -7px,
    -7px 0;
  background-color: #ffffff;
}
</style>
